import {
  type PropsWithChildren,
  createContext,
  useContext,
  useMemo,
} from 'react'
import { useQuery } from 'urql'
import type { CurrentUserQueryQuery } from '../gql/graphql'
import { CurrentUserQuery } from '../graphql/currentUser'
import { OrganizationProvider } from './useOrganization'

type CurrentUser = NonNullable<CurrentUserQueryQuery['me']>

type CurrentUserContextValue = {
  /** The logged-in operator, or undefined while loading. */
  user: CurrentUser | undefined
  /** Whether the user query is still loading. */
  loading: boolean
  /** Any error from the user query. */
  error: Error | undefined
}

const CurrentUserContext = createContext<CurrentUserContextValue>({
  user: undefined,
  loading: true,
  error: undefined,
})

/**
 * Returns the current logged-in operator.
 * Must be used within a `<CurrentUserProvider>`.
 */
export const useCurrentUser = (): CurrentUserContextValue =>
  useContext(CurrentUserContext)

type CurrentUserProviderProps = PropsWithChildren<{
  /** Skip the query (e.g. when not authenticated). */
  skip?: boolean
}>

/**
 * Fetches the `me` query and provides the operator data to the tree.
 * Also initializes the OrganizationProvider from the user's org data.
 */
export const CurrentUserProvider = ({
  children,
  skip,
}: CurrentUserProviderProps) => {
  const [result] = useQuery({
    query: CurrentUserQuery,
    pause: skip,
  })

  const user = result.data?.me ?? undefined

  const orgData = user?.organization
    ? {
        id: String(user.organization.id),
        name: user.organization.name,
        logo: user.organization.logo ?? undefined,
        primaryColor: user.organization.primaryColor ?? undefined,
        requestPreWorkAuthorization:
          user.organization.config.customerApp.requestPreWorkAuthorization,
      }
    : null

  const value = useMemo(
    () => ({ user, loading: result.fetching, error: result.error }),
    [user, result.fetching, result.error],
  )

  return (
    <CurrentUserContext.Provider value={value}>
      <OrganizationProvider initialOrganization={orgData}>
        {children}
      </OrganizationProvider>
    </CurrentUserContext.Provider>
  )
}
